// Mirrors sendifier_api/src/modules/omni-channel/schemas/flow.schema.ts — keep in sync.

export type ConditionField =
  | 'time'
  | 'day_of_week'
  | 'contact.isReturning'
  | 'contact.messageCount'
  | 'contact.hoursSinceLastMessage'
  | 'contact.country'
  | 'contact.tags'
  | 'contact.phone'
  | 'contact.email'
  | 'contact.isAssigned'
  | 'contact.assignedRole'
  | 'formula';

export type ConditionOperator =
  | 'equals' | 'not_equals'
  | 'greater_than' | 'less_than' | 'between'
  | 'contains' | 'not_contains'
  | 'in' | 'not_in'
  | 'has_value' | 'is_empty'
  | 'matches_formula';

export interface FlowCondition {
  // Either one of the fixed ConditionField values, or a generic "contact.profile.<path>"
  // dotted path into the contact's raw profile (including custom fields) — see
  // ConditionGroupsEditor.tsx's custom-field picker mode. Widened to plain string client-side
  // since TS template literal types aren't runtime-enforced anyway.
  field: ConditionField | string;
  operator: ConditionOperator;
  value: any; // unused for has_value/is_empty
}

export interface FlowConditionGroup {
  name?: string; // shown on the canvas line and in runs; default "Group N"
  conditions: FlowCondition[];
  logic: 'AND' | 'OR';
  nextNodeId?: string;
}

export type FlowNodeType =
  | 'start'
  | 'send_message'
  | 'ask_and_wait'
  | 'webhook'
  | 'update_contact'
  | 'assign_user'
  | 'assign_department'
  | 'send_dataset'
  | 'show_product'
  | 'show_catalog'
  | 'open_url'
  | 'create_task'
  | 'create_calendar_event'
  | 'find_contact_tasks'
  | 'find_contact_events'
  | 'set_context_field'
  | 'call_flow'
  | 'write_data_list'
  | 'check_language'
  | 'generate_text'
  | 'send_otp'
  | 'write_post'
  | 'pause'
  | 'ai_step'
  | 'check_time'
  | 'wait'
  | 'ai_agent'
  | 'create_order'
  | 'create_quotation'
  | 'add_to_vendor'
  | 'create_contact'
  | 'add_to_list'
  | 'remove_from_list'
  | 'send_template'
  | 'notify_team'
  | 'find_leads'
  | 'send_bulk_whatsapp'
  | 'manage_assignments'
  | 'branch'
  | 'end';

export type FlowButtonActionType = 'go_to_node' | 'visit_website' | 'call';

export interface FlowButton {
  id: string;
  label: string;
  actionType?: FlowButtonActionType; // defaults to 'go_to_node' when absent (pre-existing flows)
  nextNodeId?: string; // meaningful when actionType is 'go_to_node'
  url?: string; // meaningful when actionType is 'visit_website'
  phoneNumber?: string; // meaningful when actionType is 'call', E.164 format
}

export interface FlowNodeConfig {
  text?: string;
  mediaUrl?: string;
  mediaType?: string;
  buttons?: FlowButton[];
  nextNodeId?: string;
  // Label for the button that opens a WhatsApp LIST message — only meaningful (and required,
  // see FlowForm.tsx's validateNode) once there are more than 3 go_to_node buttons, since
  // that's when flow-engine.service.ts's send_message case switches from the raw 3-button
  // type to a list message. WhatsApp caps this at 20 characters.
  listButtonText?: string;
  saveAs?: string;
  webhookId?: string;
  // update_contact — 'profile' (default): one profile field; 'custom': custom fields of a
  // Contacts data list (same as the contact screen's Extra Fields).
  contactUpdateMode?: 'profile' | 'custom';
  contactField?: string;
  valueTemplate?: string;
  tagsMode?: 'add' | 'remove' | 'replace';
  customFieldListId?: string;
  customFieldValues?: Array<{ id: string; columnId: string; valueTemplate: string }>;
  userIds?: string[];
  departmentIds?: string[];
  setId?: string;
  productId?: string;
  catalogId?: string;
  url?: string;
  // create_task — assigns a real AssignmentTask to the contact, reusing the same
  // userIds/departmentIds fields assign_user/assign_department already use for "who".
  taskTitle?: string;
  taskDescription?: string;
  taskPriority?: 'low' | 'normal' | 'high' | 'urgent';
  taskDueDate?: string; // template string, interpolated then parsed as a date
  // create_calendar_event: 'fixed_time' books eventStartDate/eventEndDate directly;
  // 'slots' sends live available slots as a WhatsApp list message and pauses for a tap;
  // 'browser' sends a link to the calendar-booking web page (any future date) and pauses
  // until that page reports a completed booking. Optional for backward compatibility — older
  // flows without this field fall back to the original convention (both dates set = fixed
  // time, unset = slots).
  bookingMode?: 'fixed_time' | 'slots' | 'browser';
  calendarId?: string;
  eventTitle?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  slotWindowDays?: number; // 'slots' mode only — how many days ahead to offer slots for
  // Sent once booking succeeds, in ALL three modes: a downloadable .ics file followed by this
  // text. Supports {{bookedEvent.*}} interpolation. Falls back to a generic confirmation if unset.
  confirmationMessage?: string;

  // action-node result capture (create_calendar_event and future action nodes) — distinct
  // from ask_and_wait's saveAs (the contact's own reply), this captures the NODE's result.
  saveResultAs?: string;

  // set_context_field — each row writes valueTemplate (interpolated, then coerced to
  // dataType) into the flow context at fieldName, creating it if new or overriding it if a
  // prior node (e.g. ask_and_wait's saveAs) already set it.
  contextFields?: Array<{
    id: string;
    fieldName: string;
    dataType: 'string' | 'number' | 'boolean' | 'date';
    valueTemplate: string;
  }>;

  // call_flow — invokes another Flow as a subflow. callFlowInputs are interpolated against
  // THIS flow's variables and seeded into the child's fresh variables at start. The child can
  // pause across multiple contact replies just like a top-level flow; once it reaches an end
  // node, its full variables bag is written into saveResultAs here and this flow resumes at
  // nextNodeId — the node right after this one, function-call/return style.
  callFlowId?: string;
  callFlowInputs?: Array<{
    id: string;
    paramName: string;
    valueTemplate: string;
  }>;

  // write_data_list — creates/updates/finds/deletes a record in a Data List. valueTemplate
  // and match value templates are interpolated against this flow's variables at runtime, same
  // as every other *Template field. For 'find', the matched record is written into saveResultAs
  // (the existing field, reused). columnId references a DataListColumn.id on the picked list.
  dataListId?: string;
  dataListOperation?: 'create' | 'update' | 'find' | 'delete';
  dataListValueMappings?: Array<{ id: string; columnId: string; valueTemplate: string }>;
  dataListMatchCriteria?: Array<{
    id: string;
    columnId: string;
    operator: 'eq' | 'contains' | 'gt' | 'lt' | 'gte' | 'lte';
    valueTemplate: string;
  }>;

  // check_language — detects the language of languageInputTemplate (interpolated like any
  // other *Template field, e.g. "{{Start.input.text}}" or an ask_and_wait's saveAs variable)
  // and routes by matching the detected ISO 639-3 code against each languageRoutes row, in
  // order (first match wins). A row can batch multiple codes (e.g. a "French" route with just
  // ['fra'], or an "Arabic" route with ['arb']) so both "one route per language" and "one
  // route per language group" are expressible. languageDefaultNextNodeId covers everything
  // that doesn't match any row. The detected code is optionally saved via saveResultAs
  // (the existing field, reused) for use later in the flow.
  languageInputTemplate?: string;
  languageRoutes?: Array<{ id: string; name?: string; languages: string[]; nextNodeId?: string }>;
  languageDefaultNextNodeId?: string;

  // generate_text — random string written into saveResultAs (the existing field, reused).
  // 'numbers' is the one to use as an OTP code source (e.g. {{GenerateCode.saveResultAt}}
  // referenced by send_otp's Code Value field, once wired to the same variable name).
  // generatePrefix (interpolated) is prepended after generation — doesn't count against
  // generateLength.
  generateContentType?: 'text' | 'numbers' | 'mixed';
  generatePrefix?: string;
  generateLength?: number;

  // send_otp — 'template' mode sends an existing APPROVED AUTHENTICATION-category WhatsApp
  // template (the real Meta OTP UX: copy-code button + built-in expiry text), picked from the
  // business's template list; 'freeform' sends a plain interactive message with the code in
  // the body plus ONE text button (not a real clipboard action — WhatsApp has no freeform
  // equivalent). otpCodeTemplate is the code itself, e.g. "{{GenerateCode.saveResultAt}}" or a
  // literal. otpExpiryMinutes is informational only — shown in the freeform body / already
  // built into the template — the engine never enforces expiry.
  otpMode?: 'template' | 'freeform';
  otpTemplateId?: string;
  otpTemplateName?: string;
  otpTemplateLanguage?: string;
  otpCodeTemplate?: string;
  otpBodyTemplate?: string;
  otpButtonText?: string;
  otpExpiryMinutes?: number;

  // pause — stops the flow (session status 'manually_paused'); resumed from Executions, it
  // continues at nextNodeId.

  // write_post — creates a new Page post. Reuses text/mediaUrl/mediaType
  // above rather than declaring separate fields (same message+media shape
  // as send_message). postChannel is channel-generic by design but only
  // 'facebook' is implemented today; postPageId is only needed when this
  // node must target a specific page other than whichever page the flow is
  // already attached to.
  postChannel?: 'facebook';
  postPageId?: string;

  // ai_step — the connected AI model; replies use conversation/catalog context.
  aiMode?: 'classify' | 'extract' | 'reply';
  aiInputTemplate?: string; // default {{lastMessage}}
  aiInstructions?: string;
  aiCategories?: Array<{ id: string; label: string; description?: string; nextNodeId?: string }>;
  aiFields?: Array<{ id: string; name: string; description?: string }>;
  aiSendReply?: boolean;
  aiDefaultNextNodeId?: string;

  // check_time — routes on the current time in the business timezone; first matching route wins.
  timeRoutes?: FlowTimeRoute[];
  timeDefaultNextNodeId?: string;

  // wait — holds the run, then continues at nextNodeId (picked up within a minute of the time).
  // A reply during the wait goes to waitReplyNextNodeId when set; otherwise it is ignored.
  waitMode?: 'duration' | 'until_time' | 'until_open';
  waitAmount?: number;
  waitUnit?: 'minutes' | 'hours' | 'days';
  waitTime?: string; // HH:mm, business timezone
  waitDaysOfWeek?: number[]; // 0 = Sunday
  waitReplyNextNodeId?: string;

  // Custom names for connections without a name of their own ("default", "fallback", "reply").
  edgeLabels?: Record<string, string>;

  // ai_agent — holds the conversation until one of its exits (see the API schema for details).
  agentExits?: FlowAgentExit[];
  agentSilenceMinutes?: number;
  agentSilenceNextNodeId?: string;
  agentMaxTurns?: number;
  agentMaxTurnsNextNodeId?: string;
  // Who it speaks as: '' = the business, 'assigned' = the contact's AI agent, or an AI agent's id.
  agentActAs?: string;
  // How the AI shows products: 'page' (default — product card / one catalogue page) or 'chat' (in-chat lists).
  agentPresentation?: 'page' | 'chat';
  // show_product / show_catalog from the conversation: an id or a name, may hold {{variables}}.
  productRef?: string;
  catalogRef?: string;

  // create_order / create_quotation
  recordSource?: string; // variable holding the cart, default "agent.cart"
  // Manage steps: what to do (default create / add) and which record (see the API schema).
  recordOperation?: 'create' | 'auto' | 'find' | 'update' | 'cancel' | 'convert' | 'delete' | 'remove' | 'add' | 'check';
  recordOperations?: string[]; // Manage Quotation: several allowed — the conversation decides
  recordTarget?: 'latest' | 'number';
  recordNumber?: string;
  recordStatus?: string;
  recordPaymentStatus?: 'unpaid' | 'partial' | 'paid' | 'refunded';
  recordNotFoundNextNodeId?: string;
  shareMode?: 'details' | 'pdf' | 'both' | 'none';
  recordNotes?: string;
  quotationValidDays?: number;

  // add_to_vendor / create_contact
  recordFields?: { name?: string; phone?: string; email?: string; notes?: string; tags?: string };

  // update_contact (profile) — extra fields after the main one
  profileUpdates?: Array<{ id: string; contactField: string; valueTemplate?: string; tagsMode?: 'add' | 'remove' | 'replace' }>;

  // send_template
  waTemplateId?: string;
  waTemplateName?: string;
  waTemplateLanguage?: string;
  waTemplateParams?: string[];
  // A media header's file for this send (link, may hold {{variables}}); empty = the template's own.
  waHeaderMediaUrl?: string;
  waHeaderFilename?: string;
  // …or a file uploaded / a place picked here (the conversation template picker's AssetCard).
  waHeaderAsset?: { type?: 'image' | 'video' | 'document' | 'location'; url?: string; filename?: string; latitude?: number; longitude?: number; address?: string };

  // notify_team (userIds = recipients; empty = owners/admins)
  notifyTitle?: string;
  notifyBody?: string;
  // find_leads — a Lead Finder search the AI plans from leadBrief, run in the background.
  leadBrief?: string;
  leadLimit?: number;
  leadSources?: string[];
  // send_bulk_whatsapp — a campaign-style WhatsApp send (waTemplate* above), then the run waits
  // for it to finish and continues with {{bulk.sent}} / {{bulk.failed}} / {{bulk.skipped}}.
  // Template values may use {{recipient.name}} / {{recipient.<field or column>}}.
  bulkSource?: 'contacts' | 'data_list' | 'context';
  bulkTags?: string[];
  bulkTagsMode?: 'any' | 'all';
  bulkContactListId?: string;
  bulkAssignedUserId?: string;
  bulkAssignedDepartmentId?: string;
  bulkCity?: string;
  bulkCreatedWithinDays?: number;
  bulkDataListId?: string;
  bulkPhoneColumnId?: string;
  bulkNameColumnId?: string;
  bulkContextPath?: string;
  bulkPhonePath?: string;
  bulkNamePath?: string;
  bulkMessage?: string;
  bulkMax?: number;
  // manage_assignments — assign / find / get / update / transfer / complete / cancel / remove
  // assignments of any kind; result {{assignments.count}} / .ids / .first.* / .contactIds /
  // .visitIds / .recordIds / .orderIds…; asgEmptyPath: none found → recordNotFoundNextNodeId.
  asgOperation?: 'assign' | 'find' | 'get' | 'update' | 'transfer' | 'complete' | 'cancel' | 'remove';
  asgScope?: 'contact' | 'business' | 'ids';
  asgIds?: string;
  asgId?: string;
  asgTargetKinds?: string[];
  asgAssignmentTypes?: string[];
  asgStatuses?: string[];
  asgTo?: 'any' | 'users' | 'departments' | 'nobody';
  asgPriorities?: string[];
  asgOverdue?: boolean;
  asgCreatedWithinDays?: number;
  asgSources?: string[];
  asgLimit?: number;
  asgAssignKind?: string;
  asgAssignTargetIds?: string;
  asgTitle?: string;
  asgPriority?: 'low' | 'normal' | 'high' | 'urgent';
  asgDueInHours?: number;
  asgSetPriority?: 'low' | 'normal' | 'high' | 'urgent';
  asgSetDueInHours?: number;
  asgSetDueDate?: string;
  asgSetTitle?: string;
  asgSetDescription?: string;
  asgSetStatus?: 'active' | 'inProgress' | 'locked';
  asgAddTags?: string;
  asgTransferUserId?: string;
  asgTransferDepartmentId?: string;
  asgNote?: string;
  asgEmptyPath?: boolean;
  // Assign as an AI agent's handoff: its own assignments end first; assignPrevious = to who had the contact before.
  assignHandoff?: boolean;
  assignPrevious?: boolean;
}

export interface FlowAgentExit {
  id: string;
  kind: 'order' | 'quotation' | 'handoff' | 'product' | 'catalog' | 'custom';
  label: string;
  description?: string;
  nextNodeId?: string;
}

/** The exits the AI decides itself (from its own conversation steps), and their default names. */
export const AGENT_EXIT_KINDS: Array<{ kind: FlowAgentExit['kind']; label: string; hint: string }> = [
  { kind: 'order', label: 'Order ready', hint: 'The customer confirmed what they want to order (the cart is handed over).' },
  { kind: 'quotation', label: 'Wants a quotation', hint: 'Anything about a quotation: a new one, changing or cancelling theirs, or accepting it as an order (the cart and the decision are handed over).' },
  { kind: 'handoff', label: 'Wants a person', hint: 'The customer asks for a human, or the AI cannot help.' },
  { kind: 'product', label: 'Shows a product', hint: 'The AI chose a product to show: connect a Show Product step set to "From the conversation" ({{agent.product.id}}).' },
  { kind: 'catalog', label: 'Shows a catalogue', hint: 'The AI chose a catalogue to show: connect a Show Catalog step set to "From the conversation" ({{agent.catalog.id}}).' },
  { kind: 'custom', label: 'Custom', hint: 'Describe when to leave; the AI checks after each reply.' },
];

export interface FlowTimeRoute {
  id: string;
  label?: string;
  businessHours?: 'open' | 'closed';
  daysOfWeek?: number[]; // 0 = Sunday
  daysOfMonth?: number[]; // 1–31, -1 = last day
  months?: number[]; // 1–12
  timeFrom?: string; // HH:mm
  timeTo?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;
  nextNodeId?: string;
}

export interface FlowNodePosition {
  x: number;
  y: number;
}

export interface FlowNode {
  id: string;
  type: FlowNodeType;
  name: string;
  config: FlowNodeConfig;
  conditionGroups?: FlowConditionGroup[]; // only meaningful for type: 'branch'
  defaultNextNodeId?: string; // only meaningful for type: 'branch'
  position?: FlowNodePosition; // canvas position (web builder only), persisted so the layout doesn't reset on reopen
}

export interface FlowFormData {
  name: string;
  description?: string;
  nodes: FlowNode[];
  entryNodeId: string;
  isActive: boolean;
  sessionTimeoutMinutes?: number | null;
  stopOnAgentReply?: boolean;
}

export interface NodeTypeMeta {
  type: FlowNodeType;
  label: string;
  icon: string;
  description: string;
  color: string;
  /** Node types that only have a working implementation when the flow is attached to a
   * WhatsApp number — see flow-engine.service.ts's executeNode default case. Surfaced as a
   * warning badge in the picker/canvas rather than hidden, so the capability gap is visible
   * instead of silently doing nothing. */
  whatsappOnly?: boolean;
  /** Same idea as whatsappOnly but for the reverse case — a node type only
   * implemented for Facebook so far (write_post), surfaced the same way
   * (warning badge, not hidden) since the gap should be visible rather than
   * a silent no-op if used from a WhatsApp-attached flow. */
  facebookOnly?: boolean;
  /** Groups this node type in the add-node picker (search + category filter). */
  category: NodeCategory;
  /** Excluded from the add-node picker entirely — for node types that exist automatically
   * (Start) rather than being user-addable, but still need an entry here for icon/color/label
   * lookups elsewhere (FlowNodeCard, FlowForm's list view). */
  hidden?: boolean;
}

export type NodeCategory = 'messaging' | 'logic' | 'data' | 'people' | 'commerce' | 'integrations' | 'control';

export interface NodeCategoryMeta {
  id: NodeCategory;
  label: string;
  icon: string;
}

export const NODE_CATEGORY_META: NodeCategoryMeta[] = [
  { id: 'messaging', label: 'Messaging', icon: 'chatbubbles-outline' },
  { id: 'logic', label: 'Logic & Routing', icon: 'git-branch-outline' },
  { id: 'data', label: 'Data', icon: 'server-outline' },
  { id: 'people', label: 'People', icon: 'people-outline' },
  { id: 'commerce', label: 'Commerce', icon: 'cart-outline' },
  { id: 'integrations', label: 'Integrations', icon: 'code-working-outline' },
  { id: 'control', label: 'Control', icon: 'flag-outline' },
];

export const NODE_TYPE_META: NodeTypeMeta[] = [
  { type: 'start', label: 'Start', icon: 'play-circle-outline', description: 'Where the flow begins', color: '#6B7280', category: 'control', hidden: true },
  { type: 'send_message', label: 'Send Message', icon: 'chatbubble-outline', description: 'Send text, media, or buttons', color: '#2563EB', category: 'messaging' },
  { type: 'ask_and_wait', label: 'Ask & Wait', icon: 'help-circle-outline', description: 'Ask a question, save the reply as a variable', color: '#7C3AED', category: 'messaging' },
  { type: 'send_otp', label: 'Send OTP', icon: 'key-outline', description: 'Send a one-time code via an approved template or a plain message', color: '#2563EB', whatsappOnly: true, category: 'messaging' },
  { type: 'send_dataset', label: 'Send Dataset', icon: 'layers-outline', description: 'Send an existing replies dataset', color: '#2563EB', category: 'messaging' },
  { type: 'write_post', label: 'Post to Facebook', icon: 'logo-facebook', description: 'Publish a new post on a connected Facebook Page', color: '#1877F2', facebookOnly: true, category: 'messaging' },
  { type: 'open_url', label: 'Open URL', icon: 'link-outline', description: 'Send a link', color: '#4B5563', category: 'messaging' },
  { type: 'branch', label: 'Branch', icon: 'git-branch-outline', description: 'Check conditions and route accordingly', color: '#059669', category: 'logic' },
  { type: 'check_language', label: 'Check Language', icon: 'language-outline', description: 'Detect the message language and route accordingly', color: '#059669', category: 'logic' },
  { type: 'set_context_field', label: 'Set Context Field', icon: 'create-outline', description: 'Add or override a value in the flow context', color: '#0891B2', category: 'logic' },
  { type: 'call_flow', label: 'Call Flow', icon: 'git-pull-request-outline', description: 'Run another flow, then continue here', color: '#7C3AED', category: 'logic' },
  { type: 'ai_step', label: 'AI Step', icon: 'ai-bot', description: 'One quick AI decision on the latest message: sort it, pick out details, or write one reply', color: '#7C3AED', category: 'logic' },
  { type: 'ai_agent', label: 'AI Agent', icon: 'chatbubbles-outline', description: 'The AI talks with the customer — as many messages as needed — until it knows what they want, then continues to the matching step', color: '#7C3AED', category: 'logic' },
  { type: 'wait', label: 'Wait', icon: 'hourglass-outline', description: 'Wait a while, until a time, or until you open', color: '#059669', category: 'logic' },
  { type: 'check_time', label: 'Check Time', icon: 'alarm-outline', description: 'Route by business hours, day, date or time', color: '#059669', category: 'logic' },
  { type: 'generate_text', label: 'Generate Text', icon: 'shuffle-outline', description: 'Generate random text, numbers, or a mix', color: '#059669', category: 'logic' },
  { type: 'create_contact', label: 'Create Contact', icon: 'person-add-outline', description: 'Add a new contact (e.g. someone the customer refers)', color: '#0891B2', category: 'data' },
  { type: 'add_to_list', label: 'Manage List', icon: 'list-circle-outline', description: 'Add this contact to a data list, remove them, or check if they are on it', color: '#0D9488', category: 'data' },
  { type: 'remove_from_list', label: 'Remove from List', icon: 'remove-circle-outline', description: 'Remove this contact from a data list', color: '#0D9488', category: 'data', hidden: true },
  { type: 'write_data_list', label: 'Write Data List', icon: 'server-outline', description: 'Create, update, find, or delete a data list record', color: '#0D9488', category: 'data' },
  { type: 'update_contact', label: 'Update Contact', icon: 'person-circle-outline', description: 'Save a value onto the contact record', color: '#0891B2', category: 'data' },
  { type: 'find_contact_tasks', label: 'Find Contact Tasks', icon: 'list-outline', description: "Look up this contact's open tasks", color: '#B91C1C', whatsappOnly: true, category: 'data' },
  { type: 'find_contact_events', label: 'Find Contact Events', icon: 'time-outline', description: "Check this contact's upcoming calendar events", color: '#B91C1C', whatsappOnly: true, category: 'data' },
  { type: 'manage_assignments', label: 'Manage Assignments', icon: 'people-circle-outline', description: 'Assign, find, update, transfer, complete or cancel assignments of any kind — contacts, visits, orders, data-list rows… — and keep their ids', color: '#D97706', category: 'people' },
  // Replaced by Manage Assignments (its Assign); kept so existing workflows show and run them.
  { type: 'assign_user', label: 'Assign to User', icon: 'person-add-outline', description: 'Assign the conversation to a user', color: '#D97706', category: 'people', hidden: true },
  { type: 'assign_department', label: 'Assign to Department', icon: 'business-outline', description: 'Assign the conversation to a department', color: '#D97706', category: 'people', hidden: true },
  { type: 'create_task', label: 'Create Task', icon: 'checkmark-circle-outline', description: 'Create a task', color: '#B91C1C', whatsappOnly: true, category: 'people' },
  { type: 'create_calendar_event', label: 'Create Calendar Event', icon: 'calendar-outline', description: 'Book a calendar event', color: '#B91C1C', whatsappOnly: true, category: 'people' },
  { type: 'create_order', label: 'Manage Order', icon: 'receipt-outline', description: 'Create an order from the AI cart, or find, update or cancel this customer\'s order', color: '#DB2777', category: 'commerce' },
  { type: 'create_quotation', label: 'Manage Quotation', icon: 'document-text-outline', description: 'Create a quotation from the AI cart, or find, update, convert to an order or delete one', color: '#DB2777', category: 'commerce' },
  { type: 'add_to_vendor', label: 'Manage Vendor', icon: 'storefront-outline', description: 'Register this contact as a vendor, or find, update or remove their vendor record', color: '#DB2777', category: 'commerce' },
  { type: 'show_product', label: 'Show Product', icon: 'cube-outline', description: 'Send a product with its photo, price and link', color: '#DB2777', category: 'commerce' },
  { type: 'show_catalog', label: 'Show Catalog', icon: 'grid-outline', description: 'Send a catalog link', color: '#DB2777', category: 'commerce' },
  { type: 'send_template', label: 'Send Template', icon: 'mail-outline', description: 'Send an approved WhatsApp template (works after 24 hours)', color: '#2563EB', whatsappOnly: true, category: 'messaging' },
  { type: 'notify_team', label: 'Notify Team', icon: 'notifications-outline', description: 'Send an in-app alert to team members', color: '#D97706', category: 'people' },
  { type: 'send_bulk_whatsapp', label: 'Send WhatsApp to Many', icon: 'megaphone-outline', description: 'A campaign from the workflow: an approved template to contacts by filter, a data list, or a list in the workflow — then waits until it is sent', color: '#25D366', category: 'messaging' },
  { type: 'find_leads', label: 'Find Leads', icon: 'ai-bot', description: 'The AI searches the map and the web for businesses you describe (Lead Finder), in the background', color: '#0EA5E9', category: 'people' },
  { type: 'webhook', label: 'Webhook', icon: 'code-working-outline', description: 'Fire a business webhook', color: '#B91C1C', whatsappOnly: true, category: 'integrations' },
  { type: 'pause', label: 'Pause', icon: 'pause-circle-outline', description: 'Pause the flow until a team member resumes it', color: '#6B7280', category: 'control' },
  { type: 'end', label: 'End', icon: 'stop-circle-outline', description: 'Stop the flow here', color: '#6B7280', category: 'control' },
];

export const genId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export function emptyNodeConfig(type: FlowNodeType): FlowNodeConfig {
  switch (type) {
    case 'ask_and_wait':
      return { text: '', saveAs: '' };
    case 'set_context_field':
      return { contextFields: [{ id: genId('ctx'), fieldName: '', dataType: 'string', valueTemplate: '' }] };
    case 'write_data_list':
      return { dataListOperation: 'create', dataListValueMappings: [], dataListMatchCriteria: [] };
    case 'check_language':
      return { languageInputTemplate: '', languageRoutes: [] };
    case 'generate_text':
      return { generateContentType: 'numbers', generateLength: 6 };
    case 'send_otp':
      return { otpMode: 'template', otpExpiryMinutes: 5 };
    case 'write_post':
      return { postChannel: 'facebook', text: '' };
    case 'ai_step':
      return {
        aiMode: 'classify',
        aiInputTemplate: '{{lastMessage}}',
        aiCategories: [{ id: genId('cat'), label: '' }, { id: genId('cat'), label: '' }],
      };
    case 'check_time':
      return { saveResultAs: 'now', timeRoutes: [{ id: genId('time'), label: '', businessHours: 'open' }] };
    case 'update_contact':
      return { contactUpdateMode: 'profile' };
    case 'wait':
      return { waitMode: 'duration', waitAmount: 1, waitUnit: 'hours' };
    case 'ai_agent':
      return {
        saveResultAs: 'agent', agentMaxTurns: 30,
        agentExits: [
          { id: genId('exit'), kind: 'order', label: 'Order ready' },
          { id: genId('exit'), kind: 'handoff', label: 'Wants a person' },
        ],
      };
    case 'create_order':
      return { recordSource: 'agent.cart', shareMode: 'details', saveResultAs: 'order' };
    case 'create_quotation':
      return { recordSource: 'agent.cart', shareMode: 'pdf', saveResultAs: 'quotation', quotationValidDays: 7, recordOperation: 'create', recordOperations: ['create', 'update', 'delete', 'convert'] };
    case 'add_to_vendor':
      return { recordFields: { name: '{{contact.name}}' }, saveResultAs: 'vendor' };
    case 'create_contact':
      return { recordFields: { name: '', phone: '' }, saveResultAs: 'newContact' };
    case 'send_template':
      return { waTemplateParams: [] };
    case 'manage_assignments':
      return { asgOperation: 'assign', asgScope: 'contact', asgAssignKind: 'contact', saveResultAs: 'assignments' };
    case 'send_bulk_whatsapp':
      return { bulkSource: 'contacts', bulkTagsMode: 'any', waTemplateParams: [], bulkMax: 1000, saveResultAs: 'bulk' };
    default:
      return {};
  }
}

/** Every code franc-min (the reduced ~60-language build this app uses server-side) can
 * actually return — confirmed against node_modules/franc-min/data.json directly, not the
 * general 176-language franc package's docs, which list different/additional codes that this
 * build has no trigram data for and will never emit. Notably: Arabic is 'arb' (the individual
 * Standard Arabic code), NOT the 'ara' macrolanguage code a franc newcomer would reach for —
 * a route configured with 'ara' silently never matches and always falls through to the
 * default route, which is exactly the "Arabic detected as English" bug this list was built to
 * fix. Chinese/Japanese/Korean are omitted entirely — franc-min has no script/trigram data for
 * any of them, so they can never be detected; picking them would be a route that can never
 * fire. 'und' (undetermined) is an explicit, selectable outcome, distinct from a route's own
 * "no match" default fallback — matters when a business wants short/ambiguous messages
 * (franc-min needs a handful of real words to be reliable) routed differently from "everyone
 * else". Keep this list and CHECK_LANGUAGE_LABELS_BY_SCRIPT in flow-engine's check_language
 * case in sync with data.json if the franc-min dependency version ever changes. */
export const CHECK_LANGUAGE_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'eng', label: 'English' },
  { code: 'arb', label: 'Arabic' },
  { code: 'fra', label: 'French' },
  { code: 'spa', label: 'Spanish' },
  { code: 'deu', label: 'German' },
  { code: 'por', label: 'Portuguese' },
  { code: 'ita', label: 'Italian' },
  { code: 'rus', label: 'Russian' },
  { code: 'tur', label: 'Turkish' },
  { code: 'fas', label: 'Persian' },
  { code: 'urd', label: 'Urdu' },
  { code: 'hin', label: 'Hindi' },
  { code: 'ind', label: 'Indonesian' },
  { code: 'vie', label: 'Vietnamese' },
  { code: 'nld', label: 'Dutch' },
  { code: 'pol', label: 'Polish' },
  { code: 'swe', label: 'Swedish' },
  { code: 'ukr', label: 'Ukrainian' },
  { code: 'ron', label: 'Romanian' },
  { code: 'hun', label: 'Hungarian' },
  { code: 'ces', label: 'Czech' },
  { code: 'bul', label: 'Bulgarian' },
  { code: 'zlm', label: 'Malay' },
  { code: 'som', label: 'Somali' },
  { code: 'und', label: 'Undetermined / too short' },
];

export function emptyNode(type: FlowNodeType, name: string): FlowNode {
  return {
    id: genId('node'),
    type,
    name,
    config: emptyNodeConfig(type),
    conditionGroups: type === 'branch' ? [{ conditions: [{ field: 'contact.messageCount', operator: 'greater_than', value: '' }], logic: 'AND' }] : undefined,
  };
}

/** Every outgoing connection a node can have, as (label, nextNodeId, setter) triples — used
 * by both the canvas (to draw one edge per connection) and validation (to check every edge
 * points at a real node). A plain node has one "default" edge; a node with buttons has one
 * per button; a branch node has one per condition group plus a default. */
export interface NodeEdge {
  key: string; // stable within the node, e.g. "default", "button:abc123", "group:0"
  label: string;
  nextNodeId?: string;
  /** The user named this connection (group/route name, or a custom label on Next/Otherwise). */
  named?: boolean;
}

export function getNodeEdges(node: FlowNode): NodeEdge[] {
  const custom = (node.config || {}).edgeLabels || {};
  // Connections with no name of their own can be given one (edgeLabels); the rest are named by
  // their button / group / category / rule / route.
  return getNodeEdgesRaw(node).map((edge) => {
    const own = custom[edge.key]?.trim();
    return own && !/^(button|ai|time|group|lang|exit):/.test(edge.key) ? { ...edge, label: own, named: true } : edge;
  });
}

function getNodeEdgesRaw(node: FlowNode): NodeEdge[] {
  if (node.type === 'end') return [];
  if (node.type === 'branch') {
    const groupEdges = (node.conditionGroups || []).map((g, i) => ({
      key: `group:${i}`,
      label: g.name?.trim() || `Group ${i + 1}`,
      nextNodeId: g.nextNodeId,
      named: !!g.name?.trim(),
    }));
    return [...groupEdges, { key: 'default', label: 'Otherwise', nextNodeId: node.defaultNextNodeId }];
  }
  // config can be absent on documents saved before a config default existed, or on nodes
  // (like 'end') that never needed one — never assume it's present.
  const cfg = node.config || {};
  if (node.type === 'check_language') {
    const routeEdges = (cfg.languageRoutes || []).map((r, i) => ({
      key: `lang:${i}`,
      label: r.name?.trim() || (r.languages && r.languages.length > 0 ? r.languages.join(', ') : `Route ${i + 1}`),
      nextNodeId: r.nextNodeId,
      named: !!r.name?.trim(),
    }));
    return [...routeEdges, { key: 'default', label: 'Other languages', nextNodeId: cfg.languageDefaultNextNodeId }];
  }
  if (node.type === 'ai_step' && (cfg.aiMode || 'classify') === 'classify') {
    const categoryEdges = (cfg.aiCategories || []).map((c, i) => ({ key: `ai:${c.id}`, label: c.label || `Category ${i + 1}`, nextNodeId: c.nextNodeId }));
    return [...categoryEdges, { key: 'fallback', label: 'None / AI unavailable', nextNodeId: cfg.aiDefaultNextNodeId }];
  }
  if (node.type === 'ai_step') {
    return [{ key: 'default', label: 'Next', nextNodeId: cfg.nextNodeId }, { key: 'fallback', label: 'AI unavailable', nextNodeId: cfg.aiDefaultNextNodeId }];
  }
  if (node.type === 'check_time') {
    const routeEdges = (cfg.timeRoutes || []).map((r, i) => ({ key: `time:${r.id}`, label: r.label || `Rule ${i + 1}`, nextNodeId: r.nextNodeId }));
    return [...routeEdges, { key: 'fallback', label: 'Otherwise', nextNodeId: cfg.timeDefaultNextNodeId }];
  }
  if (node.type === 'pause') {
    return [{ key: 'default', label: 'After resume', nextNodeId: cfg.nextNodeId }];
  }
  if (node.type === 'wait') {
    return [{ key: 'default', label: 'After the wait', nextNodeId: cfg.nextNodeId }, { key: 'reply', label: 'If they reply', nextNodeId: cfg.waitReplyNextNodeId }];
  }
  if (['create_order', 'create_quotation', 'add_to_vendor'].includes(node.type) && !(cfg.recordOperations?.length || 0) && !['create', 'auto'].includes(cfg.recordOperation || 'create')) {
    return [{ key: 'default', label: 'Then', nextNodeId: cfg.nextNodeId }, { key: 'notfound', label: 'Not found', nextNodeId: cfg.recordNotFoundNextNodeId }];
  }
  if (node.type === 'manage_assignments' && cfg.asgEmptyPath) {
    return [{ key: 'default', label: 'Then', nextNodeId: cfg.nextNodeId }, { key: 'notfound', label: 'None found', nextNodeId: cfg.recordNotFoundNextNodeId }];
  }
  if (node.type === 'add_to_list' && cfg.recordOperation === 'check') {
    return [{ key: 'default', label: 'On the list', nextNodeId: cfg.nextNodeId }, { key: 'notfound', label: 'Not on the list', nextNodeId: cfg.recordNotFoundNextNodeId }];
  }
  if (node.type === 'ai_agent') {
    return [
      ...(cfg.agentExits || []).map((e) => ({ key: `exit:${e.id}`, label: e.label || 'Exit', nextNodeId: e.nextNodeId, named: true })),
      ...(cfg.agentSilenceMinutes ? [{ key: 'silence', label: 'Went silent', nextNodeId: cfg.agentSilenceNextNodeId }] : []),
      { key: 'maxturns', label: 'Too many turns', nextNodeId: cfg.agentMaxTurnsNextNodeId },
      { key: 'fallback', label: 'AI unavailable', nextNodeId: cfg.aiDefaultNextNodeId },
    ];
  }
  const buttons = cfg.buttons || [];
  if (buttons.length > 0) {
    // visit_website/call buttons open a URL/dialer on the device and never advance the flow
    // graph (WhatsApp doesn't report back whether they were tapped) — they're a button in the
    // config editor but not a graph edge.
    const goToNodeButtons = buttons.filter((b) => !b.actionType || b.actionType === 'go_to_node');
    const buttonEdges = goToNodeButtons.map((b) => ({ key: `button:${b.id}`, label: b.label || 'Button', nextNodeId: b.nextNodeId }));
    return cfg.nextNodeId ? [...buttonEdges, { key: 'default', label: 'No button tapped', nextNodeId: cfg.nextNodeId }] : buttonEdges;
  }
  return [{ key: 'default', label: 'Next', nextNodeId: cfg.nextNodeId }];
}

/** One insertable {{path}} entry in the variable picker. */
export interface VariableEntry {
  path: string;
  label: string;
}

/** A named group of related variables, rendered as a section in the variable picker. */
export interface VariableGroup {
  key: string;
  label: string;
  entries: VariableEntry[];
  /** True for the one group (Contact's custom fields) that needs a free-text suffix typed
   * in by the business rather than a fixed list of entries — customFields is unstructured
   * Mixed data, so there's no way to know its keys ahead of time. */
  freeTextPrefix?: string;
}

/** The fixed set of contact-profile fields always available for interpolation — mirrors
 * execContext.ruleContext.contact.profile, which flow-engine.service.ts seeds into
 * variables.contact at the start of every run (see evaluateForTarget). */
const CONTACT_VARIABLE_GROUP: VariableGroup = {
  key: 'contact',
  label: 'Contact',
  entries: [
    { path: 'contact.name', label: 'Name' },
    { path: 'contact.email', label: 'Email' },
    { path: 'contact.phone', label: 'Phone' },
    { path: 'contact.location.country', label: 'Country' },
    { path: 'contact.location.city', label: 'City' },
    { path: 'contact.tags', label: 'Tags' },
  ],
  freeTextPrefix: 'contact.customFields.',
};

/** The contact's latest message — set when the flow starts and on every reply. */
const MESSAGE_VARIABLE_GROUP: VariableGroup = {
  key: 'message',
  label: 'Message',
  entries: [{ path: 'lastMessage', label: "Contact's last message" }],
};

const USER_VARIABLE_GROUP: VariableGroup = {
  key: 'user', label: 'Running user / AI agent',
  entries: [
    { path: 'user.id', label: 'User ID' },
    { path: 'user.name', label: 'Full name' },
    { path: 'user.firstName', label: 'First name' },
    { path: 'user.lastName', label: 'Last name' },
    { path: 'user.email', label: 'Email' },
    { path: 'user.phone', label: 'Phone' },
    { path: 'user.accountType', label: 'Person / AI agent' },
    { path: 'user.role', label: 'Business role' },
    { path: 'user.departmentId', label: 'Department ID' },
    ...(['assignments', 'orders', 'quotations'] as const).flatMap(section => [
      { path: `user.${section}.count`, label: `${section}: total count` },
      { path: `user.${section}.items`, label: `${section}: latest 20 records` },
      { path: `user.${section}.hasMore`, label: `${section}: more records available` },
    ]),
  ],
};

/** For a saveResultAs-producing node type, the sub-fields worth offering in the picker —
 * only the scalar leaves that make sense inside a single text field, not raw objects/arrays
 * (interpolate() would JSON.stringify a bare object, and there's no way to loop an array in
 * a text field at all — see find_contact_tasks/find_contact_events below). Keyed by node
 * type; ask_and_wait's saveAs is handled separately since it's a flat string, not an object. */
const RESULT_SUBFIELDS_BY_NODE_TYPE: Partial<Record<FlowNodeType, Array<{ suffix: string; label: string }>>> = {
  create_calendar_event: [
    { suffix: 'title', label: 'Title' },
    { suffix: 'startDate', label: 'Start Date' },
    { suffix: 'endDate', label: 'End Date' },
    { suffix: 'location.city', label: 'Location' },
  ],
  create_task: [
    { suffix: 'taskId', label: 'Task ID' },
  ],
  find_contact_tasks: [
    { suffix: 'count', label: 'Count' },
  ],
  manage_assignments: [
    { suffix: 'count', label: 'Count' },
    { suffix: 'ids', label: 'Assignment ids' },
    { suffix: 'first.id', label: 'First: id' },
    { suffix: 'first.status', label: 'First: status' },
    { suffix: 'first.assignedTo', label: 'First: assigned to' },
    { suffix: 'contactIds', label: 'Contact ids' },
    { suffix: 'visitIds', label: 'Visit ids' },
    { suffix: 'recordIds', label: 'Data-list row ids' },
    { suffix: 'orderIds', label: 'Order ids' },
    { suffix: 'changed', label: 'Changed' },
  ],
  send_bulk_whatsapp: [
    { suffix: 'sent', label: 'Sent' },
    { suffix: 'failed', label: 'Failed' },
    { suffix: 'skipped', label: 'Skipped (no mobile)' },
    { suffix: 'recipients', label: 'Recipients' },
    { suffix: 'status', label: 'Status' },
  ],
  find_leads: [
    { suffix: 'runId', label: 'Search id' },
    { suffix: 'name', label: 'Search name' },
    { suffix: 'limit', label: 'Most results' },
  ],
  ai_agent: [
    { suffix: 'cart', label: 'Cart (products)' },
    { suffix: 'total', label: 'Cart total' },
    { suffix: 'currency', label: 'Currency' },
    { suffix: 'summary', label: 'Conversation summary' },
    { suffix: 'exit', label: 'Exit taken' },
    { suffix: 'language', label: 'Customer language' },
    { suffix: 'product.id', label: 'Product it chose to show' },
    { suffix: 'product.name', label: 'Product name' },
    { suffix: 'catalog.id', label: 'Catalogue it chose to show' },
    { suffix: 'catalog.name', label: 'Catalogue name' },
  ],
  create_order: [
    { suffix: 'number', label: 'Order number' },
    { suffix: 'status', label: 'Status' },
    { suffix: 'payment', label: 'Payment' },
    { suffix: 'total', label: 'Total' },
    { suffix: 'items', label: 'Items' },
    { suffix: 'id', label: 'Order ID' },
  ],
  create_quotation: [
    { suffix: 'number', label: 'Quotation number' },
    { suffix: 'status', label: 'Status' },
    { suffix: 'total', label: 'Total' },
    { suffix: 'validUntil', label: 'Valid until' },
    { suffix: 'orderNumber', label: 'Order number (after convert)' },
    { suffix: 'id', label: 'Quotation ID' },
  ],
  add_to_vendor: [
    { suffix: 'name', label: 'Vendor name' },
    { suffix: 'id', label: 'Vendor ID' },
  ],
  create_contact: [
    { suffix: 'name', label: 'Name' },
    { suffix: 'phone', label: 'Phone' },
    { suffix: 'id', label: 'Contact ID' },
  ],
  check_time: [
    { suffix: 'time', label: 'Time (HH:mm)' },
    { suffix: 'date', label: 'Date' },
    { suffix: 'dayName', label: 'Day name' },
    { suffix: 'dayOfMonth', label: 'Day of month' },
    { suffix: 'monthName', label: 'Month name' },
    { suffix: 'isBusinessOpen', label: 'Open now (true/false)' },
  ],
  find_contact_events: [
    { suffix: 'count', label: 'Count' },
    { suffix: 'nextEvent.title', label: 'Next Event Title' },
    { suffix: 'nextEvent.startDate', label: 'Next Event Start' },
    { suffix: 'nextEvent.endDate', label: 'Next Event End' },
    { suffix: 'nextEvent.location', label: 'Next Event Location' },
  ],
};

/** Strips a node's free-text display name down to a stable, path-safe key — same convention
 * already used for saveAs/saveResultAs (NodeConfigForm.tsx) — so it can be used as the
 * `<name>` in `{{<name>.input}}`. Must stay in sync with the equivalent sanitizer the backend
 * applies when writing that key at runtime (see node-input-tracking-backend-spec.md). */
export function sanitizeNodeName(name: string): string {
  return (name || '').replace(/[^a-zA-Z0-9_]/g, '');
}

/** Builds the full list of variables available for interpolation in the node currently being
 * edited: the fixed Contact group, plus one entry (ask_and_wait) or several (saveResultAs
 * nodes) per OTHER node in the flow that captures something — labeled with that node's own
 * name so a business can tell which "Ask & Wait" or "Book Appointment" node a value came
 * from — plus one unconditional `<name>.input` entry per OTHER node, since every node
 * automatically records which incoming edge routed the contact to it (id + text of the edge),
 * regardless of node type or config. Purely derived from `otherNodes` (already filtered to
 * exclude the node being edited), so it updates live as the business edits any other node's
 * name/saveAs/saveResultAs field — no extra plumbing needed since callers re-derive this on
 * every render. */
export function getAvailableVariables(otherNodes: FlowNode[]): VariableGroup[] {
  const collectedEntries: VariableEntry[] = [];

  for (const node of otherNodes) {
    const cfg = node.config || {};
    const label = node.name || 'Untitled node';

    const sanitizedName = sanitizeNodeName(node.name);
    if (sanitizedName) {
      collectedEntries.push({ path: `${sanitizedName}.input`, label: `${label} → Input` });
    }

    if (node.type === 'ask_and_wait' && cfg.saveAs) {
      collectedEntries.push({ path: cfg.saveAs, label: `${label} → Reply` });
      continue;
    }

    if (node.type === 'set_context_field') {
      for (const field of cfg.contextFields || []) {
        if (field.fieldName) {
          collectedEntries.push({ path: field.fieldName, label: `${label} → ${field.fieldName}` });
        }
      }
      continue;
    }

    if (node.type === 'ai_step' && cfg.saveResultAs) {
      const mode = cfg.aiMode || 'classify';
      if (mode === 'classify') collectedEntries.push({ path: `${cfg.saveResultAs}.category`, label: `${label} → Category` });
      else if (mode === 'reply') collectedEntries.push({ path: cfg.saveResultAs, label: `${label} → Reply` });
      else {
        for (const field of cfg.aiFields || []) {
          const key = field.name.trim().replace(/[^a-zA-Z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
          if (key) collectedEntries.push({ path: `${cfg.saveResultAs}.${key}`, label: `${label} → ${field.name}` });
        }
      }
      continue;
    }

    if (node.type === 'call_flow' && cfg.saveResultAs) {
      // The child flow's variable shape is arbitrary (whatever it happens to set) — unlike
      // create_calendar_event/create_task's fixed RESULT_SUBFIELDS_BY_NODE_TYPE shape, there's
      // no static list of sub-fields to offer here, so just surface the bag itself; a business
      // references a specific child variable as {{<saveResultAs>.someChildVariable}} by typing
      // it, same as any other dotted-path interpolation.
      collectedEntries.push({ path: cfg.saveResultAs, label: `${label} → Result` });
      continue;
    }

    if (cfg.saveResultAs) {
      const subfields = RESULT_SUBFIELDS_BY_NODE_TYPE[node.type];
      if (subfields) {
        for (const { suffix, label: subLabel } of subfields) {
          collectedEntries.push({ path: `${cfg.saveResultAs}.${suffix}`, label: `${label} → ${subLabel}` });
        }
      }
    }
  }

  const groups: VariableGroup[] = [CONTACT_VARIABLE_GROUP, MESSAGE_VARIABLE_GROUP, USER_VARIABLE_GROUP];
  if (collectedEntries.length > 0) {
    groups.push({ key: 'collected', label: "This Flow's Collected Values", entries: collectedEntries });
  }
  return groups;
}

const RECENT_NODE_TYPES_STORAGE_KEY = 'sendifier-flow-frequent-node-types';

/** Records that a node type was just added, for the "Frequently Used" section of the add-node
 * picker — a simple usage-count map persisted locally per-device (not per-business/server-side;
 * this is a personal builder-UI convenience, not flow data). Best-effort: any storage failure
 * is swallowed silently since this only affects a convenience ordering, never flow correctness. */
export async function recordNodeTypeUsage(type: FlowNodeType): Promise<void> {
  try {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    const raw = await AsyncStorage.getItem(RECENT_NODE_TYPES_STORAGE_KEY);
    const counts: Record<string, number> = raw ? JSON.parse(raw) : {};
    counts[type] = (counts[type] || 0) + 1;
    await AsyncStorage.setItem(RECENT_NODE_TYPES_STORAGE_KEY, JSON.stringify(counts));
  } catch {
    // best-effort only
  }
}

/** Returns up to `limit` node types (as full NodeTypeMeta entries) sorted by recorded usage
 * count, most-used first — the "Frequently Used" row atop NodeTypePicker.tsx. Empty array
 * (not an error) when nothing's been recorded yet, e.g. a brand new business/device. */
export async function getFrequentlyUsedNodeTypes(limit = 4): Promise<NodeTypeMeta[]> {
  try {
    const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
    const raw = await AsyncStorage.getItem(RECENT_NODE_TYPES_STORAGE_KEY);
    if (!raw) return [];
    const counts: Record<string, number> = JSON.parse(raw);
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([type]) => NODE_TYPE_META.find((m) => m.type === type))
      .filter((m): m is NodeTypeMeta => !!m && !m.hidden);
  } catch {
    return [];
  }
}

/** Step types worth offering right after a node of `type` — the canvas's one-click "next step"
 * suggestions when a node is selected. */
const NEXT_STEP_SUGGESTIONS: Partial<Record<FlowNodeType, FlowNodeType[]>> = {
  start: ['ai_agent', 'send_message', 'ask_and_wait'],
  send_message: ['ask_and_wait', 'branch', 'end'],
  ask_and_wait: ['ai_step', 'branch', 'update_contact'],
  ai_step: ['send_message', 'update_contact', 'manage_assignments'],
  check_time: ['send_message', 'assign_department', 'wait'],
  check_language: ['send_message', 'ask_and_wait', 'ai_step'],
  branch: ['send_message', 'manage_assignments', 'end'],
  wait: ['send_message', 'check_time', 'end'],
  ai_agent: ['create_order', 'create_quotation', 'show_product', 'show_catalog', 'manage_assignments'],
  create_order: ['notify_team', 'end'],
  create_quotation: ['notify_team', 'end'],
  add_to_vendor: ['notify_team', 'send_message', 'end'],
  create_contact: ['send_message', 'end'],
  add_to_list: ['send_message', 'end'],
  remove_from_list: ['send_message', 'end'],
  send_template: ['ask_and_wait', 'ai_agent', 'end'],
  notify_team: ['send_message', 'end'],
  find_leads: ['notify_team', 'send_message', 'end'],
  send_bulk_whatsapp: ['notify_team', 'branch', 'end'],
  manage_assignments: ['send_bulk_whatsapp', 'notify_team', 'branch', 'end'],
  webhook: ['branch', 'send_message', 'set_context_field'],
  update_contact: ['send_message', 'manage_assignments', 'end'],
  assign_user: ['send_message', 'end'],
  assign_department: ['send_message', 'end'],
  create_calendar_event: ['send_message', 'end'],
  write_data_list: ['branch', 'send_message', 'end'],
  generate_text: ['send_message', 'send_otp'],
  send_otp: ['ask_and_wait', 'branch'],
  set_context_field: ['branch', 'send_message'],
  call_flow: ['send_message', 'branch', 'end'],
  find_contact_tasks: ['branch', 'send_message'],
  find_contact_events: ['branch', 'send_message'],
  pause: ['send_message', 'end'],
};

export function suggestNextNodeTypes(type: FlowNodeType): FlowNodeType[] {
  return NEXT_STEP_SUGGESTIONS[type] || ['send_message', 'ask_and_wait', 'end'];
}

/** A run drawn over the canvas (Flow runs → Open flow): the steps it went through, in order,
 * and the connections it took. */
export interface FlowRunHighlight {
  label: string;
  nodes: Record<string, { order: number[]; error: boolean; waiting: boolean }>;
  edges: string[]; // "<fromNodeId>-><toNodeId>"
}

export function runHighlightFromSteps(label: string, steps: Array<{ nodeId: string; outcome: string }>): FlowRunHighlight {
  const nodes: FlowRunHighlight['nodes'] = {};
  const edges = new Set<string>();
  let visit = 0;
  steps.forEach((step, i) => {
    const entry = nodes[step.nodeId] || (nodes[step.nodeId] = { order: [], error: false, waiting: false });
    const previous = steps[i - 1];
    // A resumed node logs two steps (waiting, then done) — one visit.
    if (!previous || previous.nodeId !== step.nodeId) entry.order.push(++visit);
    if (step.outcome === 'error') entry.error = true;
    entry.waiting = step.outcome === 'waiting' || step.outcome === 'paused';
    if (previous && previous.nodeId !== step.nodeId) edges.add(`${previous.nodeId}->${step.nodeId}`);
  });
  return { label, nodes, edges: [...edges] };
}

/** Turns a one-shot AI Step ("Sort the message") into an AI Agent that holds the conversation:
 * each category becomes an exit to the same step. Order / quotation / "a person" categories
 * become the agent's built-in exits (decided by the agent itself — e.g. "order" only once the
 * cart has products); the rest stay custom exits described by the category. */
export function aiStepToAgent(node: FlowNode): FlowNode {
  const cfg = node.config || {};
  const used = new Set<string>();
  const kindOf = (text: string): FlowAgentExit['kind'] => {
    const t = text.toLowerCase();
    const kind: FlowAgentExit['kind'] = /quot|عرض سعر|تسعير/.test(t) ? 'quotation'
      : /order|buy|purchase|checkout|طلب|شراء|اشتري/.test(t) ? 'order'
        : /assign|human|person|agent|staff|team|موظف|تحويل|بشري|خدمة العملاء/.test(t) ? 'handoff' : 'custom';
    if (kind === 'custom' || used.has(kind)) return 'custom';
    used.add(kind);
    return kind;
  };
  const exits: FlowAgentExit[] = (cfg.aiCategories || []).filter((c) => c.label?.trim()).map((c) => {
    const kind = kindOf(`${c.label} ${c.description || ''}`);
    return { id: c.id, kind, label: c.label.trim(), ...(kind === 'custom' ? { description: (c.description || c.label).trim() } : {}), nextNodeId: c.nextNodeId };
  });
  return {
    ...node,
    type: 'ai_agent',
    name: !node.name || node.name === 'AI Step' ? 'AI Agent' : node.name,
    config: {
      aiInstructions: cfg.aiInstructions,
      agentExits: exits,
      aiDefaultNextNodeId: cfg.aiDefaultNextNodeId,
      saveResultAs: 'agent',
      agentMaxTurns: 30,
    },
  };
}
